import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useSessionKeepalive from "@/hooks/useSessionKeepalive";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (err) {
        console.warn("Auth check failed", err);
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    setMustChangePassword(Boolean(data.must_change_password));
    return data;
  };

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout API call failed (proceeding with local cleanup)", e); }
    localStorage.removeItem("token");
    setUser(false);
    setMustChangePassword(false);
  }, []);

  const handleIdle = useCallback(() => {
    toast.info("Sessione scaduta per inattività (30 min). Effettua di nuovo l'accesso.");
    logout();
  }, [logout]);

  // Keep session alive while user is active + auto-logout after 30 min idle
  useSessionKeepalive(Boolean(user && user.id), handleIdle);

  const clearMustChangePassword = () => setMustChangePassword(false);

  // Convenience flags derived from the user's role
  const isViewer = user && user.role === "viewer";
  const canWrite = Boolean(user && user.role && user.role !== "viewer");

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, formatApiError, mustChangePassword, clearMustChangePassword, canWrite, isViewer }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
